import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, ArrowRight, Mail } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import '../admin/Auth.css';

const css = `
:root {
  --sl-bg: #f9f7f5;
  --sl-panel: #ffffff;
  --sl-border: rgba(15,23,42,0.08);
  --sl-border-strong: rgba(15,23,42,0.16);
  --sl-text: #14181f;
  --sl-muted: #64748b;
  --sl-primary: #c8102e;
  --sl-shadow: 0 30px 80px -30px rgba(15,23,42,0.18), 0 8px 24px -12px rgba(200,16,46,0.15);
}

.auth-layout {
  min-height: 100vh;
  width: 100%;
  display: grid;
  grid-template-columns: 1.05fr 1fr;
  background:
    radial-gradient(1200px 600px at -10% -20%, rgba(200,16,46,0.12), transparent 60%),
    radial-gradient(900px 500px at 110% 120%, rgba(237,0,0,0.08), transparent 60%),
    linear-gradient(180deg, #ffffff 0%, #f9f7f5 100%);
  color: var(--sl-text);
  font-family: 'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  position: relative;
  overflow: hidden;
}

.auth-layout::before {
  content: '';
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(15,23,42,0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(15,23,42,0.05) 1px, transparent 1px);
  background-size: 44px 44px;
  mask-image: radial-gradient(ellipse at center, black 40%, transparent 75%);
  pointer-events: none;
}

.auth-promo {
  position: relative;
  padding: 48px 56px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border-right: 1px solid var(--sl-border);
  overflow: hidden;
  background:
    linear-gradient(135deg, rgba(15,23,42,0.68) 0%, rgba(200,16,46,0.35) 100%),
    url('https://images.unsplash.com/photo-1511765224389-37f0e77cf0eb?w=1400&q=80&auto=format&fit=crop')
    center/cover no-repeat;
  min-height: 100vh;
}

.auth-promo__overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(0,0,0,0.2) 100%);
}

.auth-promo__content {
  position: relative;
  z-index: 2;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: calc(100vh - 96px);
}

.auth-promo__logo {
  margin-bottom: 28px;
}

.auth-promo__text {
  max-width: 520px;
  margin-top: auto;
  color: #ffffff;
}

.auth-promo__tag {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  border: 1px solid rgba(255,255,255,0.24);
  background: rgba(255,255,255,0.15);
  color: #ffffff;
  border-radius: 999px;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  margin-bottom: 20px;
  backdrop-filter: blur(8px);
}

.auth-promo__headline {
  font-family: 'Fraunces', Georgia, serif;
  font-size: clamp(40px, 5vw, 64px);
  line-height: 1.02;
  letter-spacing: -0.03em;
  font-weight: 500;
  margin: 0 0 20px;
  color: #ffffff;
  text-shadow: 0 2px 10px rgba(0,0,0,0.24);
}

.auth-promo__headline em {
  font-style: normal;
  background: linear-gradient(90deg, #ffffff, #f8fafc);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.auth-promo__sub {
  color: rgba(255,255,255,0.9);
  font-size: 15px;
  line-height: 1.7;
  max-width: 460px;
  margin-bottom: 28px;
  text-shadow: 0 1px 6px rgba(0,0,0,0.24);
}

.auth-promo__stats {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
}

.auth-stat {
  padding: 12px 14px;
  border: 1px solid rgba(255,255,255,0.18);
  background: rgba(255,255,255,0.12);
  border-radius: 14px;
  min-width: 112px;
  backdrop-filter: blur(8px);
}

.auth-stat__num {
  font-family: 'Fraunces', Georgia, serif;
  font-size: 24px;
  font-weight: 500;
  color: #ffffff;
  display: block;
}

.auth-stat__lbl {
  font-size: 11px;
  color: rgba(255,255,255,0.92);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-top: 4px;
  display: block;
  text-shadow: 0 1px 6px rgba(0,0,0,0.24);
}

.auth-form-panel {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px;
}

.auth-form-wrap {
  width: 100%;
  max-width: 440px;
}

.auth-home-link {
  position: absolute;
  top: 28px;
  right: 32px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: rgba(20,24,31,0.72);
  text-decoration: none;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid rgba(15,23,42,0.08);
  background: rgba(255,255,255,0.78);
  transition: all 0.2s ease;
}

.auth-home-link:hover {
  color: var(--sl-text);
  background: #ffffff;
  border-color: rgba(15,23,42,0.16);
  transform: translateY(-1px);
}

.auth-form-header {
  margin-bottom: 36px;
}

.auth-form-title {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 6px;
  letter-spacing: -0.01em;
  color: var(--gray-900);
}

.auth-form-sub {
  font-size: 0.95rem;
  color: var(--gray-500);
}

.auth-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 28px;
}

.auth-submit-btn {
  justify-content: center;
  margin-top: 4px;
}

.auth-form-footer {
  display: flex;
  justify-content: center;
  gap: 10px;
  flex-wrap: nowrap;
  font-size: 0.875rem;
  color: var(--gray-500);
  text-align: center;
}

.auth-policy-link {
  display: inline-flex;
  align-items: center;
  background: none;
  border: none;
  color: var(--sl-primary);
  cursor: pointer;
  padding: 0;
  font: inherit;
  white-space: nowrap;
}

.auth-policy-divider {
  color: var(--gray-400);
  white-space: nowrap;
}

@media (max-width: 960px) {
  .auth-layout { grid-template-columns: 1fr; }
  .auth-promo { border-right: none; border-bottom: 1px solid var(--sl-border); padding: 40px 28px 32px; }
  .auth-form-panel { padding: 32px 20px 48px; }
  .auth-home-link { top: 20px; right: 20px; }
}

@media (max-width: 480px) {
  .auth-form-header .auth-form-title { font-size: 2rem; }
  .auth-promo { padding: 32px 20px; }
}
`;

export default function SupplierLogin() {
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [policyType, setPolicyType] = useState('privacy');
  const { loginSupplier } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/auth/supplier/login', form);
      loginSupplier(res.data.token, res.data.supplier);
      toast.success(`Welcome, ${res.data.supplier.name}!`);
      navigate('/supplier/dashboard');
    } catch (err) {
      toast.error('Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await api.post('/auth/supplier/forgot-password', { email: forgotEmail });
      toast.success('If an account exists with that email, a reset link has been sent');
      setShowForgotModal(false);
      setForgotEmail('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send reset link');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="auth-layout">
      <style>{css}</style>
      {/* Left promo - packaging/manufacturing imagery */}
      <div className="auth-promo auth-promo--supplier">
        <div className="auth-promo__overlay" />
        <div className="auth-promo__content">
          <div className="auth-promo__logo" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <img src="/chi-logo.png" alt="CHI Laminate Artflow" className="auth-logo-img" />
            <div style={{ color: '#ffffff', fontSize: '0.95rem', fontWeight: 600, letterSpacing: '0.02em', textShadow: '0 1px 6px rgba(0,0,0,0.35)' }}>
              Laminate Artflow Approval
            </div>
          </div>
          <div className="auth-promo__text">
            <span className="auth-promo__tag">Vendor Portal</span>
            <h1 className="auth-promo__headline">
              Artwork<br /><em>Delivery</em><br />Made Simple
            </h1>
            <p className="auth-promo__sub">
              Upload position proofs and colour proof scans, track approvals in real time, and stay in sync with the CHI packaging team.
            </p>
            <div className="auth-promo__stats">
              <div className="auth-stat">
                <span className="auth-stat__num">2</span>
                <span className="auth-stat__lbl">Proof Stages</span>
              </div>
              <div className="auth-stat">
                <span className="auth-stat__num">Live</span>
                <span className="auth-stat__lbl">Status Updates</span>
              </div>
              <div className="auth-stat">
                <span className="auth-stat__num">Fast</span>
                <span className="auth-stat__lbl">Turnaround</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="auth-form-panel">
        <div className="auth-form-wrap">
          <div className="auth-form-logo-sm">
            <img src="/chi-logo.png" alt="CHI Laminate Artflow" className="auth-logo-img" />
          </div>
          <div className="auth-form-header">
            <h2 className="auth-form-title">Vendor Sign In</h2>
            <p className="auth-form-sub">Access your artwork delivery dashboard</p>
          </div>

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input className="form-input" type="email" placeholder="your@company.com"
                value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                required autoComplete="email" />
            </div>

            <div className="form-group">
              <label className="form-label">Password</label>
              <div className="input-password-wrap">
                <input className="form-input" type={showPass ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={form.password} onChange={e => setForm({...form, password: e.target.value})}
                  required autoComplete="current-password" />
                <button type="button" className="input-eye-btn" onClick={() => setShowPass(!showPass)}>
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
              <button
                type="button"
                onClick={() => setShowForgotModal(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#0066cc',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  textDecoration: 'none',
                  padding: 0
                }}
              >
                Forgot password?
              </button>
            </div>

            <button type="submit" className="btn btn-primary btn-lg w-full auth-submit-btn" disabled={loading}>
              {loading ? <span className="spinner" /> : <>Sign In <ArrowRight size={16} /></>}
            </button>
          </form>

          <div style={{ textAlign: 'center', marginTop: 18, display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
              <button
                type="button"
                className="auth-policy-link"
                style={{ background: 'none', border: 'none', color: 'var(--sl-primary)', cursor: 'pointer', padding: 0, font: 'inherit' }}
                onClick={() => { setPolicyType('terms'); setShowPolicyModal(true); }}
              >
                Term of Use
              </button>
              <button
                type="button"
                className="auth-policy-link"
                style={{ background: 'none', border: 'none', color: 'var(--sl-primary)', cursor: 'pointer', padding: 0, font: 'inherit' }}
                onClick={() => { setPolicyType('privacy'); setShowPolicyModal(true); }}
              >
                Privacy Policy
              </button>
            </div>
            <div style={{ fontSize: '0.8125rem', color: 'rgba(113, 128, 150, 0.9)', marginTop: 6 }}>
              © {new Date().getFullYear()} Laminate Artflow. All rights reserved.
            </div>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <div>
                <h3 style={{ fontSize: '1.0625rem', fontWeight: 700 }}>Reset Password</h3>
                <p style={{ fontSize: '0.8125rem', color: 'var(--gray-500)', marginTop: 2 }}>
                  Enter your email to receive a password reset link
                </p>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowForgotModal(false)}>✕</button>
            </div>
            <form onSubmit={handleForgotPassword}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Email Address</label>
                  <input
                    className="form-input"
                    type="email"
                    placeholder="your@company.com"
                    value={forgotEmail}
                    onChange={e => setForgotEmail(e.target.value)}
                    required
                  />
                </div>
                <div style={{
                  background: 'var(--info-light)', borderRadius: 'var(--radius-md)',
                  padding: '12px 14px', fontSize: '0.8125rem', color: 'var(--info)', lineHeight: 1.5
                }}>
                  <strong>ℹ️ Info:</strong> We'll send a password reset link to this email. The link expires in 1 hour.
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowForgotModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={forgotLoading}>
                  {forgotLoading ? <span className="spinner" /> : <><Mail size={15} /> Send Reset Link</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPolicyModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <div>
                <h3 style={{ fontSize: '1.0625rem', fontWeight: 700 }}>
                  {policyType === 'privacy' ? 'Privacy Policy' : 'Terms of Service'}
                </h3>
                <p style={{ fontSize: '0.8125rem', color: 'var(--gray-500)', marginTop: 2 }}>
                  {policyType === 'privacy'
                    ? 'How we collect, use, and protect supplier data.'
                    : 'Rules for using the Vendor Portal and service.'}
                </p>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowPolicyModal(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ paddingTop: 0 }}>
              {policyType === 'privacy' ? (
                <>
                  <p style={{ marginBottom: 16, color: 'var(--gray-600)' }}>
                    We collect only the information needed to manage supplier artwork approvals, notify you of changes, and secure your account. Your data is stored securely and is not shared with third parties outside of the Laminate Artflow review process.
                  </p>
                  <p style={{ marginBottom: 16, color: 'var(--gray-600)' }}>
                    Please review this policy before signing in. If you have questions about how we handle your information, contact your account administrator.
                  </p>
                </>
              ) : (
                <>
                  <p style={{ marginBottom: 16, color: 'var(--gray-600)' }}>
                    By using the Vendor Portal, you agree to follow the approval workflow, keep your login credentials secure, and only upload valid artwork files. Misuse may result in access restrictions.
                  </p>
                  <p style={{ marginBottom: 16, color: 'var(--gray-600)' }}>
                    This service is provided to support collaboration with the project team. All access is governed by the terms set by the Laminate Artflow supplier agreement.
                  </p>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowPolicyModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
